/*
 * BWH Gavehjælperen
 * ─────────────────
 * Chat-baseret gaveguide. Fase 1 (AI): Giorgio afdækker gaven og foreslår vine via
 * {apiBase}/api/gavehjaelper. Fase 2 (faste trin): gaveæske → tilbehør → kort →
 * levering → resume → checkout via shoppens egen kurv (/cart/add.js).
 *
 * Konfiguration og alle tekster kommer fra sections/bwh-gavehjaelperen.liquid.
 * Renderes i Shadow DOM, så hverken temaets eller apps' CSS kan påvirke den.
 */
(function () {
  'use strict';

  if (customElements.get('bwh-gavehjaelperen')) return;

  // ── Hjælpere ──────────────────────────────────────────────────────────────
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k.slice(0, 2) === 'on') node.addEventListener(k.slice(2), v);
        else node.setAttribute(k, v === true ? '' : v);
      });
    }
    (children || []).forEach(function (c) {
      if (c === null || c === undefined || c === false) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Let markdown: **fed**, *kursiv*, punktlister og afsnit. Escaper først (AI-output er utroværdigt).
  function md(text) {
    var html = escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
    return html
      .split(/\n{2,}/)
      .map(function (block) {
        if (/^\s*[-•] /m.test(block)) {
          var items = block.split('\n').filter(function (l) { return l.trim(); });
          return '<ul>' + items.map(function (l) { return '<li>' + l.replace(/^\s*[-•]\s+/, '') + '</li>'; }).join('') + '</ul>';
        }
        return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
      })
      .join('');
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function fill(template, product) {
    return String(template || '').replace(/__PRODUCT__/g, product);
  }

  // ── Custom element ────────────────────────────────────────────────────────
  class BwhGavehjaelperen extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var cfgEl = document.getElementById(this.dataset.config);
      this.cfg = JSON.parse(cfgEl.textContent);
      this.t = this.cfg.t;
      this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      this.rate = parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1;

      this.sessionId = uuid();
      this.messages = [];      // historik til AI'en
      this.giftState = {};     // Giorgios løbende viden om gaven
      this.sel = { product: null, box: null, accessories: [], card: null, delivery: null, address: null };
      this.editing = false;
      this.busy = false;

      this.shadow = this.attachShadow({ mode: 'open' });
      this.render();
      this.start();
    }

    // ── Skelet ──────────────────────────────────────────────────────────────
    render() {
      var cfg = this.cfg, t = this.t, a = cfg.assistant, h = cfg.header;

      this.shadow.appendChild(el('link', { rel: 'stylesheet', href: cfg.cssUrl }));

      // Portræt/ikon kan slås fra i Customizeren ("Vis portræt eller ikon")
      var avatar = a.showAvatar === false ? null : a.avatar
        ? el('img', { class: 'gh-avatar', src: a.avatar, alt: a.name, width: 56, height: 56 })
        : el('div', { class: 'gh-avatar gh-avatar--initial', 'aria-hidden': 'true', text: (a.name || 'G').charAt(0) });

      this.thread = el('div', { class: 'gh-thread', role: 'log', 'aria-live': 'polite' });

      this.input = el('textarea', {
        class: 'gh-input',
        rows: 1,
        maxlength: 800,
        placeholder: t.inputPlaceholder,
        'aria-label': t.inputPlaceholder,
      });
      this.sendBtn = el('button', { class: 'gh-send', type: 'submit', 'aria-label': t.sendAria, disabled: true }, [
        el('span', { 'aria-hidden': 'true', text: '→' }),
      ]);
      this.composer = el('form', { class: 'gh-composer' }, [
        el('div', { class: 'gh-composer__row' }, [this.input, this.sendBtn]),
        t.privacyNote ? el('p', { class: 'gh-privacy', text: t.privacyNote }) : null,
      ]);

      var self = this;
      this.input.addEventListener('input', function () {
        self.input.style.height = 'auto';
        self.input.style.height = Math.min(self.input.scrollHeight, 160) + 'px';
        self.sendBtn.disabled = self.busy || !self.input.value.trim();
      });
      this.input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          self.composer.requestSubmit();
        }
      });
      this.composer.addEventListener('submit', function (e) {
        e.preventDefault();
        var text = self.input.value.trim();
        if (!text || self.busy) return;
        self.input.value = '';
        self.input.style.height = 'auto';
        self.sendBtn.disabled = true;
        self.sendToGiorgio(text, 'typed');
      });

      this.shadow.appendChild(
        el('div', { class: 'gh' }, [
          el('header', { class: 'gh-head' }, [
            h.eyebrow ? el('div', { class: 'gh-eyebrow', text: h.eyebrow }) : null,
            h.heading ? el('h2', { class: 'gh-heading', text: h.heading }) : null,
            h.intro ? el('p', { class: 'gh-intro', text: h.intro }) : null,
            avatar || a.name || a.title ? el('div', { class: 'gh-agent' }, [
              avatar,
              el('div', {}, [
                a.name ? el('div', { class: 'gh-agent__name', text: a.name }) : null,
                a.title ? el('div', { class: 'gh-agent__title', text: a.title }) : null,
              ]),
            ]) : null,
          ]),
          this.thread,
          this.composer,
        ])
      );

      this.watchComposer();
    }

    // Mobil: chatfeltet ligger fast i bunden (som en app). Tråden får plads til feltet,
    // og feltet løftes over tastaturet på iOS, hvor fixed-elementer ellers ender bag det.
    watchComposer() {
      var self = this, host = this, vv = window.visualViewport;
      var setHeight = function () {
        self.composerH = self.composer.hidden ? 0 : self.composer.offsetHeight;
        host.style.setProperty('--gh-composer-h', self.composerH + 'px');
      };
      if (window.ResizeObserver) new ResizeObserver(setHeight).observe(this.composer);
      setHeight();
      if (!vv) return;
      var setKeyboard = function () {
        var kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        host.style.setProperty('--gh-kb', Math.round(kb) + 'px');
      };
      vv.addEventListener('resize', setKeyboard);
      vv.addEventListener('scroll', setKeyboard);
    }

    start() {
      var self = this;
      var flow = this.cfg.flow || {};
      var replies = (flow.occasions || []).filter(function (o) { return o.label; }).map(function (o) {
        return { label: o.label, action: function () { self.scriptOccasion(o); } };
      });
      this.botMessage(this.cfg.welcome, { quickReplies: replies });
      this.track('start', {}, {
        market: this.cfg.country,
        locale: this.cfg.locale,
        currency: this.cfg.currency,
        landing_path: location.pathname,
        last_step: 'start',
      });
      setTimeout(function () { self.input.focus({ preventScroll: true }); }, 50);
    }

    // ── Beskeder ────────────────────────────────────────────────────────────
    row(who, content) {
      var row = el('div', { class: 'gh-row gh-row--' + who }, [content]);
      this.thread.appendChild(row);
      return row;
    }

    userMessage(text) {
      this.row('user', el('div', { class: 'gh-bubble gh-bubble--user', text: text }));
    }

    // Giorgios besked; widget (fx produktkort eller valg) lægges under teksten
    botMessage(text, opts) {
      opts = opts || {};
      var bubble = el('div', { class: 'gh-bubble gh-bubble--bot' });
      if (text) {
        var body = el('div', { class: 'gh-text' });
        body.innerHTML = md(text);
        bubble.appendChild(body);
      }
      if (opts.widget) bubble.appendChild(opts.widget);
      var row = this.row('bot', bubble);
      if (opts.quickReplies && opts.quickReplies.length) this.showQuickReplies(opts.quickReplies);
      this.scrollTo(row);
      return row;
    }

    // Hurtigsvar: tekst (sendes til Giorgio) eller { label, action } (håndteres lokalt, uden AI)
    showQuickReplies(replies) {
      var self = this;
      this.clearQuickReplies();
      this.chips = el('div', { class: 'gh-chips' }, replies.map(function (r) {
        var label = typeof r === 'string' ? r : r.label;
        return el('button', {
          type: 'button',
          class: 'gh-chip',
          text: label,
          onclick: function () {
            if (self.busy) return;
            self.track('quick_reply', { text: label });
            if (typeof r === 'string') self.sendToGiorgio(r, 'quick_reply');
            else r.action();
          },
        });
      }));
      this.thread.appendChild(this.chips);
    }

    // ── Lokalt hurtigsvar-flow: anledning → år → pris (ingen ventetid) ──────
    // Kundens valg og Giorgios faste svar lægges i historikken, så AI'en kender sammenhængen.
    userSays(text) {
      this.clearQuickReplies();
      this.userMessage(text);
      this.messages.push({ role: 'user', content: text, source: 'quick_reply' });
    }

    botSays(text, replies) {
      this.messages.push({ role: 'assistant', content: text });
      this.botMessage(text, { quickReplies: replies });
    }

    scriptOccasion(o) {
      var self = this, f = this.cfg.flow, year = new Date().getFullYear();
      this.userSays(o.label);
      this.giftState = { occasion: o.label };

      var nums = function (list) {
        return String(list || '').split(',').map(function (n) { return parseInt(n, 10); }).filter(function (n) { return n > 0; });
      };
      var ageChip = function (n) {
        var label = String(f.ageLabel || '__N__').replace(/__N__/g, n);
        return { label: label, action: function () { self.scriptYear(year - n, label); } };
      };
      var other = function (label) {
        return { label: label, action: function () { self.scriptOther(label); } };
      };
      var noYear = { label: f.yearNotImportant, action: function () { self.scriptYear(0, f.yearNotImportant); } };

      if (o.key === 'birthday') {
        this.botSays(f.birthdayQuestion, nums(f.birthdayAges).map(ageChip).concat([other(f.otherAge), noYear]));
      } else if (o.key === 'anniversary') {
        this.botSays(f.anniversaryQuestion, nums(f.anniversaryYears).map(ageChip).concat([other(f.otherYear), noYear]));
      } else {
        var thisYear = String(f.weddingThisYear || '').replace(/__YEAR__/g, year);
        this.botSays(f.weddingQuestion, [
          { label: thisYear, action: function () { self.scriptYear(year, thisYear); } },
          other(f.otherYear),
          noYear,
        ]);
      }
    }

    scriptYear(year, label) {
      var f = this.cfg.flow;
      this.userSays(label);
      this.giftState.year = year || 0;
      this.giftState.year_matters = year ? 'yes' : 'no';
      var confirm = year ? String(f.yearConfirm || '').replace(/__YEAR__/g, year) : f.noYearConfirm;
      this.botSays(confirm + '\n\n' + f.priceQuestion, this.priceChips());
    }

    // "Anden alder/andet år": kunden skriver selv – derfra tager Giorgio (AI) over
    scriptOther(label) {
      this.userSays(label);
      this.botSays(this.cfg.flow.askOther);
      this.input.focus();
    }

    // Prisintervaller i kundens valuta: DKK-intervaller omregnet og afrundet til pæne tal
    priceChips() {
      var self = this, f = this.cfg.flow, rate = this.rate || 1;
      var nice = function (v) {
        if (rate === 1) return v;
        var mag = Math.pow(10, Math.floor(Math.log10(v)));
        var steps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
        return steps.map(function (s) { return s * mag; }).reduce(function (a, b) { return Math.abs(b - v) < Math.abs(a - v) ? b : a; });
      };
      var fmt = function (v) { return self.money(v * 100, true); };
      var a = nice(1000 * rate), b = nice(2000 * rate), c = nice(4000 * rate);
      var bands = [
        { min: 0, max: a, label: String(f.priceUnder).replace('__A__', fmt(a)) },
        { min: a, max: b, label: String(f.priceBetween).replace('__A__', fmt(a)).replace('__B__', fmt(b)) },
        { min: b, max: c, label: String(f.priceBetween).replace('__A__', fmt(b)).replace('__B__', fmt(c)) },
        { min: c, max: 0, label: String(f.priceOver).replace('__A__', fmt(c)) },
      ];
      return bands.map(function (band) {
        return { label: band.label, action: function () { self.scriptPrice(band); } };
      });
    }

    // Prisvalg: nu kaldes Giorgio – serveren har søgt vinene på forhånd, så der kun er ét AI-kald
    scriptPrice(band) {
      this.giftState.budget_min = band.min;
      this.giftState.budget_max = band.max;
      this.sendToGiorgio(band.label, 'quick_reply', {
        year: this.giftState.year || 0,
        budget_min: band.min,
        budget_max: band.max,
      });
    }

    clearQuickReplies() {
      if (this.chips) { this.chips.remove(); this.chips = null; }
    }

    // Prikker – med tekst ("Vi leder efter forslag til dig"), når Giorgio søger vine
    showTyping(label) {
      if (this.typing) return this.setTypingLabel(label);
      this.typingLabel = el('span', { class: 'gh-typing__label' });
      this.typing = this.row('bot', el('div', { class: 'gh-typing', role: 'status', 'aria-label': this.t.typing }, [
        el('span', { class: 'gh-typing__dots', 'aria-hidden': 'true' }, [el('span'), el('span'), el('span')]),
        this.typingLabel,
      ]));
      this.setTypingLabel(label);
      this.scrollTo(this.typing);
    }

    setTypingLabel(label) {
      if (!this.typingLabel) return;
      this.typingLabel.textContent = label || '';
      this.typingLabel.hidden = !label;
    }

    hideTyping() {
      if (this.typing) { this.typing.remove(); this.typing = null; this.typingLabel = null; }
    }

    scrollTo(node) {
      var self = this;
      requestAnimationFrame(function () {
        var rect = node.getBoundingClientRect();
        var view = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        // Vis starten af nye beskeder, men scroll kun hvis de ligger uden for skærmen (eller bag chatfeltet)
        if (rect.top < 0 || rect.bottom > view - Math.max(self.composerH || 0, 120)) {
          node.scrollIntoView({ behavior: 'smooth', block: rect.height > view * 0.6 ? 'start' : 'center' });
        }
      });
    }

    setComposer(visible) {
      this.composer.hidden = !visible;
      this.composerH = visible ? this.composer.offsetHeight : 0;
      this.style.setProperty('--gh-composer-h', this.composerH + 'px');
    }

    // ── Fase 1: samtale med Giorgio ─────────────────────────────────────────
    sendToGiorgio(text, source, prefetch) {
      var self = this;
      this.clearQuickReplies();
      this.userMessage(text);
      this.messages.push({ role: 'user', content: text, source: source });
      this.busy = true;
      // Ved prisvalg søger serveren vine med det samme – sig det til kunden
      this.showTyping(prefetch ? this.t.searching : '');

      fetch(this.cfg.apiBase.replace(/\/$/, '') + '/api/gavehjaelper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson' },
        body: JSON.stringify({
          sessionId: this.sessionId,
          messages: this.messages,
          state: this.giftState,
          prefetch: prefetch || null,
          context: {
            currency: this.cfg.currency,
            rate: this.rate,
            country: this.cfg.country,
            locale: this.cfg.locale,
            path: location.pathname,
          },
        }),
      })
        .then(function (r) {
          if (r.status === 429) { var e = new Error('rate_limited'); e.rateLimited = true; throw e; }
          if (!r.ok) throw new Error('HTTP ' + r.status);
          // Streaming (NDJSON) – ellers almindeligt JSON-svar som fallback
          var type = r.headers.get('content-type') || '';
          if (type.indexOf('ndjson') !== -1 && r.body && r.body.getReader) return self.readStream(r.body);
          return r.json().then(function (data) { self.handleReply(data, null); });
        })
        .catch(function (err) {
          console.error('Gavehjælperen:', err);
          self.hideTyping();
          if (self.live) { self.live.row.remove(); self.live = null; }
          self.busy = false;
          // Fjern den ubesvarede besked fra historikken, så den kan sendes igen
          self.messages.pop();
          self.botMessage(err && err.rateLimited ? (self.t.rateLimited || self.t.errorGeneric) : self.t.errorGeneric);
          self.sendBtn.disabled = !self.input.value.trim();
        });
    }

    // Læser Giorgios svar linje for linje og viser teksten, mens den bliver skrevet
    readStream(body) {
      var self = this, reader = body.getReader(), decoder = new TextDecoder(), buffer = '', done = false;
      this.live = null;

      function handle(event) {
        if (event.type === 'delta') {
          if (!self.live) {
            self.hideTyping();
            var text = el('div', { class: 'gh-text' });
            var bubble = el('div', { class: 'gh-bubble gh-bubble--bot' }, [text]);
            self.live = { row: self.row('bot', bubble), bubble: bubble, body: text, text: '' };
            self.scrollTo(self.live.row);
          }
          self.live.text += event.text;
          self.live.body.innerHTML = md(self.live.text);
        } else if (event.type === 'reset' && self.live) {
          self.live.row.remove();
          self.live = null;
          self.showTyping();
        } else if (event.type === 'status') {
          if (!self.live) self.showTyping(self.t.searching);
        } else if (event.type === 'products') {
          // Beskeden er skrevet, men vinforslagene (med begrundelser) er stadig på vej
          if (self.live && !self.live.loader) {
            self.live.loader = self.productsLoader();
            self.live.bubble.appendChild(self.live.loader);
            self.scrollTo(self.live.loader);
          } else if (!self.live) {
            self.showTyping(self.t.loadingProducts);
          }
        } else if (event.type === 'done') {
          done = true;
          self.handleReply(event, self.live);
          self.live = null;
        } else if (event.type === 'error') {
          throw new Error('stream_error');
        }
      }

      function pump() {
        return reader.read().then(function (res) {
          buffer += decoder.decode(res.value || new Uint8Array(), { stream: !res.done });
          var lines = buffer.split('\n');
          buffer = lines.pop();
          lines.forEach(function (line) { if (line.trim()) handle(JSON.parse(line)); });
          if (res.done) {
            if (buffer.trim()) handle(JSON.parse(buffer));
            if (!done) throw new Error('stream_incomplete');
            return;
          }
          return pump();
        });
      }
      return pump();
    }

    // Færdigt svar: tekst (evt. allerede streamet ind i "live"), produktkort og hurtigsvar
    handleReply(data, live) {
      this.hideTyping();
      this.busy = false;
      if (data.state) this.giftState = data.state;
      var products = data.products || [];
      // Giorgio skal kunne huske hvilke vine han har vist
      var memory = data.reply + (products.length
        ? '\n\n[Viste vine: ' + products.map(function (p) { return p.handle; }).join(', ') + ']'
        : '');
      this.messages.push({ role: 'assistant', content: memory });

      if (live) {
        if (live.loader) live.loader.remove();
        live.body.innerHTML = md(data.reply);
        if (products.length) live.bubble.appendChild(this.productList(products));
        if (data.quickReplies && data.quickReplies.length) this.showQuickReplies(data.quickReplies);
        if (products.length) this.scrollTo(live.row);
      } else {
        this.botMessage(data.reply, {
          widget: products.length ? this.productList(products) : null,
          quickReplies: data.quickReplies || [],
        });
      }
      this.sendBtn.disabled = !this.input.value.trim();
    }

    // Pladsholder mens vinforslagene hentes: tekst med prikker + et "skelet" af et produktkort
    productsLoader() {
      var skeleton = el('div', { class: 'gh-skeleton', 'aria-hidden': 'true' }, [
        el('div', { class: 'gh-skeleton__img' }),
        el('div', { class: 'gh-skeleton__body' }, [
          el('span', { class: 'gh-skeleton__line gh-skeleton__line--short' }),
          el('span', { class: 'gh-skeleton__line gh-skeleton__line--title' }),
          el('span', { class: 'gh-skeleton__line' }),
          el('span', { class: 'gh-skeleton__line' }),
          el('span', { class: 'gh-skeleton__line gh-skeleton__line--mid' }),
        ]),
      ]);
      return el('div', { class: 'gh-loading', role: 'status' }, [
        el('div', { class: 'gh-loading__label' }, [
          el('span', { class: 'gh-typing__dots', 'aria-hidden': 'true' }, [el('span'), el('span'), el('span')]),
          el('span', { text: this.t.loadingProducts }),
        ]),
        skeleton,
      ]);
    }

    productList(products) {
      var self = this;
      var list = el('div', { class: 'gh-products' });
      products.forEach(function (p) { list.appendChild(self.productCard(p)); });
      return list;
    }

    productCard(p) {
      var self = this, t = this.t;
      var term = this.termFactory(p);
      var meta = [
        p.vintage ? [document.createTextNode(p.vintage)] : null,
        p.region ? term('region', p.region) : null,
        p.country ? term('country', p.country) : null,
      ].filter(Boolean).reduce(function (acc, part, i) {
        return acc.concat(i ? [document.createTextNode(' · ')] : [], part);
      }, []);
      var price = el('span', { class: 'gh-product__price' });
      var selectBtn = el('button', { type: 'button', class: 'gh-btn gh-btn--primary gh-btn--small', text: t.selectBtn, disabled: true });

      var card = el('article', { class: 'gh-product' }, [
        p.image ? el('button', {
          type: 'button',
          class: 'gh-product__zoom',
          'aria-label': fill(t.zoomImage, p.title),
          onclick: function () { self.openLightbox(p.image, p.imageAlt || p.title); },
        }, [
          el('img', { class: 'gh-product__img', src: p.image, alt: p.imageAlt || p.title, loading: 'lazy' }),
          el('span', { class: 'gh-product__zoom-icon', 'aria-hidden': 'true' }, [this.zoomIcon()]),
        ]) : null,
        el('div', { class: 'gh-product__body' }, [
          meta.length ? el('div', { class: 'gh-product__meta' }, meta) : null,
          el('h3', { class: 'gh-product__title', text: p.title }),
          p.producer ? el('div', { class: 'gh-product__sub' }, term('producer', p.producer)) : null,
          p.reason ? el('p', { class: 'gh-product__reason', text: p.reason }) : null,
          this.productDetails(p),
          el('div', { class: 'gh-product__foot' }, [
            price,
            el('div', { class: 'gh-product__actions' }, [selectBtn]),
          ]),
        ]),
      ]);

      // Hent pris og lagerstatus i kundens valuta fra shoppen selv
      fetch(this.root + 'products/' + p.handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) {
          if (!prod) return;
          var variant = prod.variants.filter(function (v) { return String(v.id) === String(p.variantId); })[0]
            || prod.variants.filter(function (v) { return v.available; })[0];
          if (!variant || !variant.available) {
            price.textContent = t.soldOut;
            card.classList.add('is-sold-out');
            return;
          }
          var item = {
            handle: p.handle,
            title: prod.title,
            variantId: variant.id,
            price: variant.price,
            image: p.image,
          };
          price.textContent = self.money(variant.price);
          selectBtn.disabled = false;
          selectBtn.addEventListener('click', function () { self.selectProduct(item); });
        })
        .catch(function () { price.textContent = ''; });

      return card;
    }

    // Lup-ikon i hjørnet af vinbilledet
    zoomIcon() {
      var ns = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '14');
      svg.setAttribute('height', '14');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      ['M11 4a7 7 0 1 0 0 14a7 7 0 1 0 0-14z', 'M20 20l-4-4', 'M11 8v6', 'M8 11h6'].forEach(function (d) {
        var path = document.createElementNS(ns, 'path');
        path.setAttribute('d', d);
        svg.appendChild(path);
      });
      return svg;
    }

    // Vinbilledet i stor størrelse. Luk med ✕, tryk udenfor eller Escape.
    openLightbox(src, alt) {
      var self = this, t = this.t;
      var prevFocus = this.shadow.activeElement;
      var html = document.documentElement, prevOverflow = html.style.overflow;
      var closeBtn = el('button', { type: 'button', class: 'gh-lightbox__close', 'aria-label': t.closeImage || 'Luk', text: '✕' });
      var box = el('div', { class: 'gh-lightbox', role: 'dialog', 'aria-modal': 'true', 'aria-label': alt }, [
        el('img', { class: 'gh-lightbox__img', src: src, alt: alt }),
        closeBtn,
      ]);
      function close() {
        box.remove();
        html.style.overflow = prevOverflow;
        document.removeEventListener('keydown', onKey);
        if (prevFocus) prevFocus.focus({ preventScroll: true });
      }
      function onKey(e) { if (e.key === 'Escape') close(); }
      box.addEventListener('click', function (e) { if (e.target !== box.querySelector('.gh-lightbox__img')) close(); });
      document.addEventListener('keydown', onKey);
      html.style.overflow = 'hidden';
      self.shadow.appendChild(box);
      closeBtn.focus({ preventScroll: true });
      this.track('image_zoom', {});
    }

    // Klikbare navne (producent, region, område, druer, klassifikation): et tryk folder
    // beskrivelsen ud lige under linjen. Navne uden beskrivelse vises som almindelig tekst.
    // Returnerer en funktion: term(kind, fallbackText) → liste af noder.
    termFactory(p) {
      var about = (p.details && p.details.about) || {};
      var box = el('div', { class: 'gh-info', hidden: true });
      var title = el('div', { class: 'gh-details__label' });
      var body = el('p');
      box.appendChild(title);
      box.appendChild(body);
      var active = null;

      return function (kind, fallback) {
        var list = (about[kind] || []).filter(function (a) { return a && a.name && a.text; });
        if (!list.length) return [document.createTextNode(fallback)];
        return list.reduce(function (acc, a, i) {
          var btn = el('button', { type: 'button', class: 'gh-term', 'aria-expanded': 'false', text: a.name });
          btn.addEventListener('click', function () {
            if (active) active.setAttribute('aria-expanded', 'false');
            if (active === btn) { box.hidden = true; active = null; return; }
            title.textContent = a.name;
            body.textContent = a.text;
            btn.parentNode.after(box); // under den linje, der blev trykket på
            box.hidden = false;
            btn.setAttribute('aria-expanded', 'true');
            active = btn;
          });
          return acc.concat(i ? [document.createTextNode(', ')] : [], [btn]);
        }, []);
      };
    }

    // "Vis detaljer": fakta øverst, derefter beskrivelserne samlet – vinen, producenten,
    // landet, regionen, området og druerne (samme kilder som produktsiden). Kun felter med indhold.
    productDetails(p) {
      var t = this.t, d = p.details || {}, about = d.about || {};
      var facts = [
        [t.dProducerName, p.producer],
        [t.dCountry, d.country],
        [t.dRegion, d.region],
        [t.dArea, d.area],
        [t.dVineyard, d.vineyard],
        [t.dGrape, d.grape],
        [t.dClassification, d.classification],
        [t.dTasting, d.tastingNotes],
        [t.dAlcohol, d.alcohol ? String(d.alcohol).replace(/\s*%?$/, ' %') : null],
        [t.dServing, d.serving],
      ].filter(function (f) { return f[0] && f[1]; });

      var list = function (key) { return (about[key] || []).filter(function (a) { return a && a.text; }); };
      var producer = list('producer');
      if (!producer.length && d.producerDescription) producer = [{ name: p.producer, text: d.producerDescription }];
      var stories = [
        [t.dDescription, d.description ? [{ text: d.description }] : []],
        [t.dProducer, producer],
        [t.dAboutCountry, list('country')],
        [t.dAboutRegion, list('region')],
        [t.dAboutArea, list('area')],
        [t.dAboutGrape, list('grape')],
        [t.dAboutClassification, list('classification')],
      ].filter(function (s) { return s[0] && s[1].length; });
      if (!facts.length && !stories.length) return null;

      var panel = el('div', { class: 'gh-details', hidden: true }, [
        facts.length ? el('dl', { class: 'gh-details__facts' }, facts.reduce(function (acc, f) {
          return acc.concat([el('dt', { text: f[0] }), el('dd', { text: f[1] })]);
        }, [])) : null,
      ].concat(stories.map(function (s) {
        return el('section', { class: 'gh-details__text' }, [el('div', { class: 'gh-details__label', text: s[0] })]
          .concat(s[1].reduce(function (acc, a) {
            return acc.concat([
              a.name ? el('h4', { class: 'gh-details__name', text: a.name }) : null,
              el('p', { text: a.text }),
            ]);
          }, [])));
      })));

      var label = el('span', { text: t.detailsShow });
      var toggle = el('button', { type: 'button', class: 'gh-details__toggle', 'aria-expanded': 'false' }, [
        label,
        el('span', { class: 'gh-details__chevron', 'aria-hidden': 'true', text: '▾' }),
      ]);
      toggle.addEventListener('click', function () {
        var open = panel.hidden;
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        label.textContent = open ? t.detailsHide : t.detailsShow;
      });
      return el('div', { class: 'gh-details-wrap' }, [toggle, panel]);
    }

    // ── Fase 2: faste trin ──────────────────────────────────────────────────
    selectProduct(item) {
      if (this.busy) return;
      this.clearQuickReplies();
      this.sel.product = item;
      this.userMessage(fill(this.t.userSelected, item.title));
      this.messages.push({ role: 'user', content: fill(this.t.userSelected, item.title), source: 'typed' });
      this.messages.push({ role: 'assistant', content: fill(this.t.selectedMessage, item.title) });
      this.track('product_selected', { handle: item.handle, price: item.price }, { selected_handle: item.handle, last_step: 'product' });
      this.botMessage(fill(this.t.selectedMessage, item.title));
      this.setComposer(false);

      if (this.editing) return this.showSummary();
      this.stepBox();
    }

    // Afslut et trin: lås widget'en, vis kundens svar og gå videre
    answer(widgetRow, text, next) {
      if (widgetRow) widgetRow.classList.add('is-done');
      if (text) this.userMessage(text);
      if (this.editing) return this.showSummary();
      next.call(this);
    }

    stepBox() {
      var self = this, t = this.t, boxes = this.cfg.boxes || [];
      if (!boxes.length) return this.stepAccessories();
      var row;
      var options = boxes.map(function (b) {
        return self.optionCard(b, function () {
          self.sel.box = b;
          self.track('gift_box', { name: b.name }, { gift_box: b.name, last_step: 'gift_box' });
          self.answer(row, b.name, self.stepAccessories);
        });
      });
      options.push(el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--ghost',
        text: t.noBox,
        onclick: function () {
          self.sel.box = null;
          self.track('gift_box', { name: null }, { gift_box: 'none', last_step: 'gift_box' });
          self.answer(row, t.noBox, self.stepAccessories);
        },
      }));
      row = this.botMessage(t.boxQuestion, { widget: el('div', { class: 'gh-options' }, options) });
    }

    stepAccessories() {
      var self = this, t = this.t, items = this.cfg.accessories || [];
      if (!items.length) return this.stepCard();
      var chosen = this.sel.accessories.slice();
      var row;
      var cards = items.map(function (a) {
        var card = self.optionCard(a, function () {
          var i = chosen.indexOf(a);
          if (i === -1) chosen.push(a); else chosen.splice(i, 1);
          card.classList.toggle('is-selected', i === -1);
          card.setAttribute('aria-pressed', i === -1 ? 'true' : 'false');
          confirm.disabled = !chosen.length;
        });
        if (chosen.indexOf(a) !== -1) card.classList.add('is-selected');
        return card;
      });
      var confirm = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--primary',
        text: t.accConfirm,
        disabled: !chosen.length,
        onclick: function () {
          self.sel.accessories = chosen;
          var names = chosen.map(function (a) { return a.name; });
          self.track('accessories', { names: names }, { accessories: names, last_step: 'accessories' });
          self.answer(row, names.join(', '), self.stepCard);
        },
      });
      var skip = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--ghost',
        text: t.accSkip,
        onclick: function () {
          self.sel.accessories = [];
          self.track('accessories', { names: [] }, { accessories: [], last_step: 'accessories' });
          self.answer(row, t.accSkip, self.stepCard);
        },
      });
      row = this.botMessage(t.accQuestion, {
        widget: el('div', {}, [el('div', { class: 'gh-options' }, cards), el('div', { class: 'gh-actions' }, [confirm, skip])]),
      });
    }

    stepCard() {
      var self = this, t = this.t;
      var row;
      var yes = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--primary',
        text: t.cardYes + (this.cfg.card.mode === 'product' && this.cfg.card.price ? ' (' + this.money(this.cfg.card.price) + ')' : ''),
        onclick: function () {
          row.classList.add('is-done');
          self.userMessage(t.cardYes);
          self.stepCardText();
        },
      });
      var no = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--ghost',
        text: t.cardNo,
        onclick: function () {
          self.sel.card = null;
          self.track('card', { wanted: false }, { has_card: false, last_step: 'card' });
          self.answer(row, t.cardNo, self.stepDelivery);
        },
      });
      row = this.botMessage(t.cardQuestion, { widget: el('div', { class: 'gh-actions' }, [yes, no]) });
    }

    stepCardText() {
      var self = this, t = this.t, max = this.cfg.card.maxChars || 250;
      var row;
      var area = el('textarea', { class: 'gh-field__input gh-card-text', rows: 4, maxlength: max, placeholder: t.cardPlaceholder, 'aria-label': t.cardPrompt });
      area.value = (this.sel.card && this.sel.card.text) || '';
      var counter = el('div', { class: 'gh-counter', text: area.value.length + ' / ' + max });
      var error = el('p', { class: 'gh-error', hidden: true, text: t.cardEmpty });
      area.addEventListener('input', function () {
        counter.textContent = area.value.length + ' / ' + max;
        error.hidden = true;
      });
      var save = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--primary',
        text: t.cardConfirm,
        onclick: function () {
          var text = area.value.trim();
          if (!text) { error.hidden = false; area.focus(); return; }
          self.sel.card = { text: text };
          area.disabled = true;
          // Kortets tekst logges ikke – kun at der er et kort og længden
          self.track('card', { wanted: true, length: text.length }, { has_card: true, last_step: 'card' });
          self.answer(row, '“' + text + '”', self.stepDelivery);
        },
      });
      row = this.botMessage(t.cardPrompt, { widget: el('div', { class: 'gh-form' }, [area, counter, error, el('div', { class: 'gh-actions' }, [save])]) });
      setTimeout(function () { area.focus({ preventScroll: true }); }, 50);
    }

    stepDelivery() {
      var self = this, t = this.t;
      var row;
      var mine = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--primary',
        text: t.deliverySelf,
        onclick: function () {
          self.sel.delivery = 'self';
          self.sel.address = null;
          self.track('delivery', { to: 'self' }, { delivery: 'self', last_step: 'delivery' });
          row.classList.add('is-done');
          self.userMessage(t.deliverySelf);
          if (t.deliverySelfNote) self.botMessage(t.deliverySelfNote);
          self.showSummary();
        },
      });
      var theirs = el('button', {
        type: 'button',
        class: 'gh-btn gh-btn--primary',
        text: t.deliveryRecipient,
        onclick: function () {
          self.sel.delivery = 'recipient';
          self.track('delivery', { to: 'recipient' }, { delivery: 'recipient', last_step: 'delivery' });
          row.classList.add('is-done');
          self.userMessage(t.deliveryRecipient);
          self.stepAddress();
        },
      });
      row = this.botMessage(t.deliveryQuestion, { widget: el('div', { class: 'gh-actions gh-actions--stack' }, [mine, theirs]) });
    }

    stepAddress() {
      var self = this, t = this.t, cfg = this.cfg;
      var prev = this.sel.address || {};
      var row;

      function field(name, label, opts) {
        opts = opts || {};
        var input = opts.select || el('input', {
          class: 'gh-field__input',
          name: name,
          type: opts.type || 'text',
          autocomplete: opts.autocomplete || 'off',
          required: !opts.optional,
        });
        if (!opts.select) input.value = prev[name] || '';
        return el('label', { class: 'gh-field' + (opts.wide ? ' gh-field--wide' : '') }, [
          el('span', { class: 'gh-field__label', text: label + (opts.optional ? ' (' + t.optional + ')' : '') }),
          input,
        ]);
      }

      var country = el('select', { class: 'gh-field__input', name: 'countryCode', autocomplete: 'off' },
        (cfg.countries.length ? cfg.countries : [{ code: cfg.country, name: cfg.country }]).map(function (c) {
          return el('option', { value: c.code, text: c.name, selected: (prev.countryCode || cfg.country) === c.code });
        })
      );

      var error = el('p', { class: 'gh-error', hidden: true, text: t.addressInvalid });
      var form = el('form', { class: 'gh-form gh-address', novalidate: true }, [
        el('div', { class: 'gh-grid' }, [
          field('firstName', t.fieldFirstName),
          field('lastName', t.fieldLastName),
          field('company', t.fieldCompany, { optional: true, wide: true }),
          field('address1', t.fieldAddress1, { wide: true }),
          field('address2', t.fieldAddress2, { optional: true, wide: true }),
          field('zip', t.fieldZip),
          field('city', t.fieldCity),
          field('countryCode', t.fieldCountry, { select: country }),
          field('phone', t.fieldPhone, { optional: true, type: 'tel' }),
        ]),
        error,
        el('div', { class: 'gh-actions' }, [el('button', { type: 'submit', class: 'gh-btn gh-btn--primary', text: t.addressConfirm })]),
      ]);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var data = {};
        Array.prototype.forEach.call(form.elements, function (f) { if (f.name) data[f.name] = f.value.trim(); });
        if (!data.firstName || !data.lastName || !data.address1 || !data.zip || !data.city) {
          error.hidden = false;
          return;
        }
        self.sel.address = data;
        Array.prototype.forEach.call(form.elements, function (f) { f.disabled = true; });
        // Adressen logges ikke – kun land
        self.track('delivery', { to: 'recipient', country: data.countryCode }, { last_step: 'address' });
        self.answer(row, self.formatAddress(data), self.showSummary);
      });

      row = this.botMessage(t.addressPrompt, { widget: form });
    }

    formatAddress(a) {
      var countryName = (this.cfg.countries.filter(function (c) { return c.code === a.countryCode; })[0] || {}).name || a.countryCode;
      return [
        [a.firstName, a.lastName].join(' '),
        a.company,
        a.address1,
        a.address2,
        [a.zip, a.city].join(' '),
        countryName,
      ].filter(function (x) { return x && x.trim(); }).join(', ');
    }

    // ── Resume og checkout ──────────────────────────────────────────────────
    total() {
      var s = this.sel, sum = s.product ? s.product.price : 0;
      if (s.box) sum += s.box.price;
      s.accessories.forEach(function (a) { sum += a.price; });
      if (s.card && this.cfg.card.mode === 'product') sum += this.cfg.card.price;
      return sum;
    }

    showSummary() {
      var self = this, t = this.t, s = this.sel;
      this.editing = false;
      if (this.summaryRow) this.summaryRow.classList.add('is-done');

      function line(label, value, price, onEdit) {
        return el('div', { class: 'gh-sum__line' }, [
          el('div', { class: 'gh-sum__label', text: label }),
          el('div', { class: 'gh-sum__value', text: value }),
          el('div', { class: 'gh-sum__price', text: price ? self.money(price) : '' }),
          el('button', { type: 'button', class: 'gh-sum__edit', text: t.edit, onclick: onEdit }),
        ]);
      }
      function edit(step) {
        return function () {
          self.editing = true;
          self.summaryRow.classList.add('is-done');
          self.track('edit', { step: step.name });
          step.call(self);
        };
      }

      var deliveryText = s.delivery === 'recipient' && s.address ? t.deliveryRecipient + ': ' + this.formatAddress(s.address) : t.deliverySelf;
      var cardPrice = s.card && this.cfg.card.mode === 'product' ? this.cfg.card.price : 0;

      var checkoutBtn = el('button', { type: 'button', class: 'gh-btn gh-btn--primary gh-btn--wide', text: t.checkoutBtn });
      var error = el('p', { class: 'gh-error', hidden: true, text: t.checkoutError });
      checkoutBtn.addEventListener('click', function () { self.checkout(checkoutBtn, error); });

      var summary = el('div', { class: 'gh-sum' }, [
        line(t.summaryWine, s.product.title, s.product.price, function () { self.editWine(); }),
        this.cfg.boxes.length ? line(t.summaryBox, s.box ? s.box.name : t.none, s.box && s.box.price, edit(this.stepBox)) : null,
        this.cfg.accessories.length
          ? line(t.summaryAcc, s.accessories.length ? s.accessories.map(function (a) { return a.name; }).join(', ') : t.none,
            s.accessories.reduce(function (n, a) { return n + a.price; }, 0), edit(this.stepAccessories))
          : null,
        line(t.summaryCard, s.card ? '“' + s.card.text + '”' : t.none, cardPrice, edit(this.stepCard)),
        line(t.summaryDelivery, deliveryText, 0, edit(this.stepDelivery)),
        el('div', { class: 'gh-sum__total' }, [
          el('span', { text: t.summaryTotal }),
          el('strong', { text: this.money(this.total()) }),
        ]),
        checkoutBtn,
        error,
        t.checkoutNote ? el('p', { class: 'gh-note', text: t.checkoutNote }) : null,
        el('button', { type: 'button', class: 'gh-restart', text: t.restart, onclick: function () { self.restart(); } }),
      ]);

      this.track('summary', { total: this.total() }, { last_step: 'summary', cart_value: this.total() / 100 });
      this.summaryRow = this.botMessage(t.summaryIntro, { widget: summary });
    }

    editWine() {
      this.editing = true;
      this.summaryRow.classList.add('is-done');
      this.track('edit', { step: 'wine' });
      this.botMessage(this.t.editWineMessage);
      this.setComposer(true);
      this.input.focus();
    }

    restart() {
      this.track('restart', {});
      this.sessionId = uuid();
      this.messages = [];
      this.giftState = {};
      this.sel = { product: null, box: null, accessories: [], card: null, delivery: null, address: null };
      this.editing = false;
      this.summaryRow = null;
      this.thread.innerHTML = '';
      this.setComposer(true);
      this.start();
    }

    checkout(btn, error) {
      var self = this, t = this.t, s = this.sel;
      if (this.busy) return;
      this.busy = true;
      btn.disabled = true;
      btn.textContent = t.checkoutBusy;
      error.hidden = true;

      var giftProps = {};
      giftProps[t.propGift] = s.product.title;
      var wineProps = {};
      wineProps[t.propGift] = '✓';
      if (s.card && this.cfg.card.mode === 'free') wineProps[t.propCard] = s.card.text;

      var items = [{ id: s.product.variantId, quantity: 1, properties: wineProps }];
      if (s.box) items.push({ id: s.box.variantId, quantity: 1, properties: forGift() });
      s.accessories.forEach(function (a) { items.push({ id: a.variantId, quantity: 1, properties: forGift() }); });
      if (s.card && this.cfg.card.mode === 'product') {
        var cardProps = forGift();
        cardProps[t.propCard] = s.card.text;
        items.push({ id: this.cfg.card.variantId, quantity: 1, properties: cardProps });
      }
      function forGift() { var p = {}; p[t.propForGift] = s.product.title; return p; }

      var attributes = { _gavehjaelper_session: this.sessionId };
      attributes[t.attrDelivery] = s.delivery === 'recipient' ? t.deliveryRecipient : t.deliverySelf;
      attributes[t.attrRecipient] = s.delivery === 'recipient' && s.address ? this.formatAddress(s.address) + (s.address.phone ? ', ' + s.address.phone : '') : '';

      this.track('checkout', { total: this.total(), items: items.length }, { checkout_clicked: true, last_step: 'checkout', cart_value: this.total() / 100 });

      this.cartFetch('cart/add.js', { items: items })
        .then(function () { return self.cartFetch('cart/update.js', { attributes: attributes }); })
        .then(function () { return s.delivery === 'recipient' ? self.prefillAddress(s.address) : null; })
        .then(function () { window.location.href = '/checkout'; })
        .catch(function (err) {
          console.error('Gavehjælperen checkout:', err);
          self.track('checkout_error', { message: String(err && err.message).slice(0, 200) });
          self.busy = false;
          btn.disabled = false;
          btn.textContent = t.checkoutBtn;
          error.hidden = false;
        });
    }

    cartFetch(path, body) {
      return fetch(this.root + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || r.status); });
        return r.json();
      });
    }

    // Udfyld modtagerens adresse i checkout via Storefront API (samme kurv som /cart.js).
    // Fejler det, fortsætter vi – adressen står stadig på ordren som attribut.
    prefillAddress(a) {
      var cfg = this.cfg;
      if (!cfg.storefrontToken || !a) return Promise.resolve();
      return fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.token || cart.token.indexOf('?key=') === -1) return;
          var address = {
            firstName: a.firstName,
            lastName: a.lastName,
            address1: a.address1,
            city: a.city,
            zip: a.zip,
            countryCode: a.countryCode,
          };
          if (a.company) address.company = a.company;
          if (a.address2) address.address2 = a.address2;
          if (a.phone) address.phone = a.phone;
          return fetch('/api/' + cfg.storefrontApiVersion + '/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.storefrontToken },
            body: JSON.stringify({
              query: 'mutation($id: ID!, $addresses: [CartSelectableAddressInput!]!) { cartDeliveryAddressesAdd(cartId: $id, addresses: $addresses) { userErrors { field message } } }',
              variables: {
                id: 'gid://shopify/Cart/' + cart.token,
                addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: address } }],
              },
            }),
          })
            .then(function (r) { return r.json(); })
            .then(function (res) {
              var errs = (res.errors || []).concat((res.data && res.data.cartDeliveryAddressesAdd && res.data.cartDeliveryAddressesAdd.userErrors) || []);
              if (errs.length) console.warn('Gavehjælperen: adresse kunne ikke forudfyldes', errs);
            });
        })
        .catch(function (err) { console.warn('Gavehjælperen: adresse kunne ikke forudfyldes', err); });
    }

    // ── Diverse ─────────────────────────────────────────────────────────────
    optionCard(item, onClick) {
      return el('button', { type: 'button', class: 'gh-option', onclick: onClick, 'aria-pressed': 'false' }, [
        item.image ? el('img', { class: 'gh-option__img', src: item.image, alt: '', loading: 'lazy' }) : null,
        el('span', { class: 'gh-option__body' }, [
          el('span', { class: 'gh-option__name', text: item.name }),
          item.description ? el('span', { class: 'gh-option__desc', text: item.description }) : null,
        ]),
        el('span', { class: 'gh-option__price', text: this.money(item.price) }),
      ]);
    }

    money(cents, whole) {
      var amount = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.cfg.locale || 'da', {
          style: 'currency',
          currency: this.cfg.currency || 'DKK',
          minimumFractionDigits: !whole && amount % 1 ? 2 : 0,
          maximumFractionDigits: whole ? 0 : 2,
        }).format(amount);
      } catch (e) {
        return amount.toFixed(2) + ' ' + this.cfg.currency;
      }
    }

    track(type, data, session) {
      try {
        fetch(this.cfg.apiBase.replace(/\/$/, '') + '/api/gavehjaelper/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId, type: type, data: data || {}, session: session || {} }),
          keepalive: true,
        }).catch(function () {});
      } catch (e) { /* logning må aldrig stoppe flowet */ }
    }
  }

  customElements.define('bwh-gavehjaelperen', BwhGavehjaelperen);
})();
